import { z } from 'zod';
/** Owner-approved regular-league rules (2026-09-30). Legacy scoring remains separate. */
export type LeagueScoringMode = 'social' | 'tournament';
export type HandState = 'eligible' | 'dead' | 'erroneous_dead_call';
export interface LeagueGame {
  gameNumber: number;
  outcome: 'win' | 'wall' | 'incomplete';
  players: { playerId: string; handState: HandState }[];
  winningHand?: {
    playerId: string;
    cardValue: number;
    selfPicked: boolean;
    heavenly: boolean;
    jokerless: boolean;
    singlesPairs: boolean;
    exposures: 0 | 1 | 2 | 3;
    discarderId?: string | undefined;
    misnamedDiscard?: boolean | undefined;
  } | undefined;
}
export interface LeagueGameResult {
  outcome: LeagueGame['outcome'];
  playerId: string;
  gameNumber: number;
  earnings: number;
  deductions: { reason: 'dead_hand' | 'winning_discard' | 'misnamed_discard'; amount: number }[];
  won: boolean;
}

/** No free-form net total: earnings and each distinct penalty remain auditable evidence. */
export function calculateLeagueGame(mode: LeagueScoringMode, game: LeagueGame): LeagueGameResult[] {
  if (mode !== 'social' && mode !== 'tournament') throw new Error('Choose a scoring mode.');
  if (!Number.isInteger(game.gameNumber) || game.gameNumber < 1 || game.gameNumber > 8) throw new Error('Choose game 1–8.');
  if (game.players.length < 3 || game.players.length > 4 || new Set(game.players.map(p => p.playerId)).size !== game.players.length) throw new Error('A game requires three or four distinct players.');
  if (game.players.some(p => !p.playerId || !['eligible', 'dead', 'erroneous_dead_call'].includes(p.handState))) throw new Error('Choose each player’s hand status.');
  if (!['win', 'wall', 'incomplete'].includes(game.outcome)) throw new Error('Choose the game outcome.');
  const hand = game.winningHand;
  if (game.outcome === 'win') {
    if (!hand || !game.players.some(p => p.playerId === hand.playerId && p.handState === 'eligible')) throw new Error('Select an eligible winner.');
    if (!Number.isSafeInteger(hand.cardValue) || hand.cardValue <= 0) throw new Error('Enter the positive value printed on the card.');
    if (![0, 1, 2, 3].includes(hand.exposures)) throw new Error('Choose the winner’s exposure count.');
    if (hand.heavenly && !hand.selfPicked) throw new Error('A Heavenly Hand is self-picked.');
    if (hand.selfPicked && (hand.discarderId || hand.misnamedDiscard)) throw new Error('A self-picked hand has no winning discarder.');
    if (!hand.selfPicked && !game.players.some(p => p.playerId === hand.discarderId && p.playerId !== hand.playerId && p.handState === 'eligible')) throw new Error('Select the player who discarded the winning tile.');

  } else if (hand) throw new Error('Only a winning game has a winning hand.');

  return game.players.map(player => {
    const won = game.outcome === 'win' && hand?.playerId === player.playerId;
    const earnings = won && hand
      ? hand.cardValue + (hand.selfPicked && !hand.heavenly ? 10 : 0) + (hand.jokerless && !hand.singlesPairs ? 20 : 0)
      : game.outcome === 'wall' && player.handState === 'eligible' ? 10 : 0;
    const deductions: LeagueGameResult['deductions'] = [];
    // Approved product interpretation: incomplete games override dead-hand deductions.
    if (mode === 'tournament' && game.outcome !== 'incomplete') {
      if (player.handState === 'dead') deductions.push({ reason: 'dead_hand', amount: 10 });
      if (hand?.discarderId === player.playerId) deductions.push(hand.misnamedDiscard
        // Approved product interpretation: misnaming REPLACES, never stacks with, the discard penalty.
        ? { reason: 'misnamed_discard', amount: 25 }
        : { reason: 'winning_discard', amount: hand.exposures < 2 ? 10 : hand.exposures === 2 ? 20 : 25 });
    }
    return { outcome: game.outcome, playerId: player.playerId, gameNumber: game.gameNumber, earnings, deductions, won };
  });
}

export function leagueSessionTotal(mode: LeagueScoringMode, games: LeagueGameResult[]) {
  if (new Set(games.map(g => g.gameNumber)).size !== games.length || new Set(games.map(g => g.playerId)).size > 1) throw new Error('Use one result per game for one player.');
  if (mode !== 'social' && mode !== 'tournament') throw new Error('Choose a scoring mode.');
  for (const game of games) {
    if (!Number.isSafeInteger(game.earnings) || game.earnings < 0 || !Number.isInteger(game.gameNumber) || game.gameNumber < 1 || game.gameNumber > 8) throw new Error('Invalid game earnings or number.');
    if (new Set(game.deductions.map(d => d.reason)).size !== game.deductions.length) throw new Error('Do not deduct the same penalty twice.');
    if (game.deductions.some(d => !Number.isSafeInteger(d.amount) || d.amount <= 0)) throw new Error('Deductions must be positive magnitudes.');
  }
  const completed = games.filter(g => g.outcome !== 'incomplete');
  const best = [...completed].sort((a, b) => b.earnings - a.earnings || a.gameNumber - b.gameNumber).slice(0, 4);
  const earnings = best.reduce((sum, g) => sum + g.earnings, 0);
  const deductions = mode === 'tournament' ? completed.reduce((sum, g) => sum + g.deductions.reduce((n, d) => n + d.amount, 0), 0) : 0;
  return { points: earnings - deductions, earnings, deductions, handsWon: completed.filter(g => g.won).length, gamesPlayed: completed.length, countedGames: best.map(g => g.gameNumber).sort((a, b) => a - b), complete: completed.length > 0 };
}

export interface LeagueWinnerEvidence {
  playerId: string;
  points: number;
  highestWinningHand: number;
  wins: number;
  deductions: number;
}
/** Caller supplies approved evidence only. Session places deliberately ignore league tiebreakers. */
export function placeLeagueResults(rows: LeagueWinnerEvidence[], scope: 'session' | LeagueScoringMode) {
  const compare = (a: LeagueWinnerEvidence, b: LeagueWinnerEvidence) => b.points - a.points || (scope === 'session' ? 0 : compareLeagueEvidence(a,b,scope));
  const sorted = [...rows].sort(compare);
  let rank = 0;
  return sorted.map((row, index) => {
    if (!index || compare(sorted[index - 1]!, row) !== 0) rank = index + 1;
    const tied = (index > 0 && compare(sorted[index - 1]!, row) === 0) || (index + 1 < sorted.length && compare(row, sorted[index + 1]!) === 0);
    return { ...row, rank, tied, winner: rank <= 2 };
  });
}

/** All recorded games count as tiebreak evidence, even when their earnings were dropped. */
export function leagueWinnerEvidence(playerId: string, approvedGames: LeagueGameResult[], approvedTotal: number): LeagueWinnerEvidence {
  if (approvedGames.some(g => g.playerId !== playerId)) throw new Error('Use evidence for this player only.');
  return {
    playerId, points: approvedTotal,
    highestWinningHand: Math.max(0, ...approvedGames.filter(g => g.won).map(g => g.earnings)),
    wins: approvedGames.filter(g => g.won).length,
    deductions: approvedGames.reduce((sum, g) => sum + g.deductions.reduce((n, d) => n + d.amount, 0), 0),
  };
}

export interface ScoringEvidence { mode: LeagueScoringMode; game: LeagueGame; result: LeagueGameResult }

/** Shared HTTP validation for both score-entry surfaces. */
export const leagueGameSchema = z.object({
  gameNumber: z.number().int().min(1).max(8),
  outcome: z.enum(['win', 'wall', 'incomplete']),
  players: z.array(z.object({ playerId: z.string().uuid(), handState: z.enum(['eligible', 'dead', 'erroneous_dead_call']) })).min(3).max(4),
  winningHand: z.object({ playerId: z.string().uuid(), cardValue: z.number().int().positive().max(1000), selfPicked: z.boolean(), heavenly: z.boolean(), jokerless: z.boolean(), singlesPairs: z.boolean(), exposures: z.union([z.literal(0),z.literal(1),z.literal(2),z.literal(3)]), discarderId: z.string().uuid().optional(), misnamedDiscard: z.boolean().optional() }).optional(),
});

export function compareLeagueEvidence(a: Pick<LeagueWinnerEvidence,'highestWinningHand'|'wins'|'deductions'>, b: Pick<LeagueWinnerEvidence,'highestWinningHand'|'wins'|'deductions'>, mode: LeagueScoringMode) {
  return b.highestWinningHand-a.highestWinningHand || b.wins-a.wins || (mode==='tournament'?a.deductions-b.deductions:0);
}
